import { AbstractType, Signal, Type } from '@angular/core';
import { GridDataSource } from './grid-data-source';
import { Translation } from '@softline/ui-core';
import { Entity, IsoDate, IsoDateTime, IsoDateTimeTz } from '@softline/core';
import { GridCellRenderer } from '../abstraction/grid-cell-renderer';

export interface Grid<TRow extends object> {
  columns: Signal<Column<TRow>[]>;
  dataSource: GridDataSource<TRow>
}

export interface ValueColumnBase<TRow extends object, TValue> {
  colId: string;
  type: string;
  header: Signal<Translation>;
  getValue: (row: TRow) => TValue;
  cellClass?: string;
  headerClass?: string;

  rowGroup?: boolean;
  enableRowGroup?: boolean;
  hide?: boolean;

  cellRenderer?: AbstractType<GridCellRenderer<TRow, TValue>>
}

export type Column<TRow extends object> = StringColumn<TRow> | NumberColumn<TRow> | DateColumn<TRow> | DateTimeColumn<TRow, IsoDateTime | IsoDateTimeTz> | GroupColumn<TRow> | ObjectColumn<TRow, object> | AmountColumn<TRow> | QuantityColumn<TRow> | EntityColumn<TRow, Entity>;
export type ValueColumn<TRow extends object> = StringColumn<TRow> | NumberColumn<TRow> | DateColumn<TRow> | DateTimeColumn<TRow, IsoDateTime | IsoDateTimeTz> | ObjectColumn<TRow, object> | AmountColumn<TRow> | QuantityColumn<TRow> | EntityColumn<TRow, Entity>;

export interface ServerSideColumn {
  serverSideField: string;
  serverSideAlias?: string;
  serverSideSortField?: string;
}

export interface GroupColumnChild {
  columnGroupShow? : 'open' | 'closed'
}

export interface StringColumn<TRow extends object> extends ValueColumnBase<TRow, string>{
  type: 'string';
  aggFunc?: string;
  format?: (value: string) => string | Signal<string>;
}

export interface NumberColumn<TRow extends object> extends ValueColumnBase<TRow, number>{
  type: 'number';
  aggFunc?: string;
  format?: string;
}

export interface AmountColumn<TRow extends object> extends ValueColumnBase<TRow, number>{
  type: 'amount';
  getCurrency:((row: TRow | undefined) => string | undefined);
  aggFunc?: string;
  format?: string;
}

export interface QuantityColumn<TRow extends object> extends ValueColumnBase<TRow, number>{
  type: 'quantity';
  getUnit: (row: TRow | undefined) => string | undefined;
  aggFunc?: string;
  format?: string;
}

export interface DateColumn<TRow extends object> extends ValueColumnBase<TRow, IsoDate>{
  type: 'date';
  format?: string;
}

export interface DateTimeColumn<TRow extends object, TValue extends IsoDateTime | IsoDateTimeTz> extends ValueColumnBase<TRow, TValue>{
  type: 'dateTime';
  format?: string;
}

export interface ObjectColumn<TRow extends object, TValue extends object> extends ValueColumnBase<TRow, TValue>{
  type: 'object';
  format: (value: TValue) => string | Signal<string>;
  sortBy: (a: TValue) => number | string;
}

export interface GroupColumn<TRow extends object> {
  type: 'group';
  header: Signal<Translation>;
  headerClass?: string | null;
  children: (Column<TRow> & GroupColumnChild)[];
  hide?: boolean;
}

export interface EntityColumn<TRow extends object, TValue extends Entity> extends ValueColumnBase<TRow, TValue>{
  type: 'entity';
  format: (value: TValue) => string | Signal<string>;
  sortBy: (a: TValue) => number | string;
  children: (Column<TValue> & GroupColumnChild)[];
}

export function isServerSideColumn(column: unknown): column is ServerSideColumn {
  return !!(column as ServerSideColumn)?.serverSideField;
}
